import { DataEditContextTextField } from 'Components/reusable/DataContext/fields/DataEditContextTextField'
import Grid from '@mui/material/Grid2'
import React, { useEffect, useState } from 'react'
import { ISearchDegree, ISearchCourse } from 'types/searchInterfaces'
import * as Yup from 'yup'
import DataContext from 'Components/reusable/DataContext'
import LoadingIndicator from 'Components/reusable/LoadingIndicator'
import AddButton from 'Components/reusable/IconButtons/AddButton'
import { useTranslation } from 'react-i18next'
import { useSearchData } from 'Components/General/SearchProvider/SearchProvider'

/** @notExported */
interface IEditEducationFieldsProps {
  /** The function to add a new item. */
  onAdd: (newItem: ISearchDegree | ISearchCourse) => void
  /** The type of education. */
  type: 'degree' | 'course'
  /** Whether to show the add button. */
  noButton?: boolean
  /** Whether to show inset label. */
  insetLabel?: boolean
}

/**
 * The EducationSearchRequirement component.
 *
 * @param onAdd - The function to add a new item.
 * @param type - The type of education.
 * @returns The EducationSearchRequirement component.
 * @notExported
 */
const EducationSearchRequirement: React.FC<IEditEducationFieldsProps> = ({ onAdd, type, noButton, insetLabel }) => {
  const [data, setData] = useState<ISearchDegree | ISearchCourse | null>(null)
  const [schema, setSchema] = useState<Yup.ObjectSchema>()
  const { t, i18n } = useTranslation()
  const { resetFields, setResetFields } = useSearchData()

  useEffect(() => {
    switch (type) {
      case 'degree':
        setData({
          degree: '',
        } as ISearchDegree)

        setSchema(
          Yup.object({
            degree: Yup.string().nullable(),
          })
        )

        break

      case 'course':
        setData({
          course: '',
        } as ISearchCourse)

        setSchema(
          Yup.object({
            course: Yup.string().nullable(),
          })
        )

        break
    }
  }, [i18n, type])

  useEffect(() => {
    if (resetFields) {
      switch (type) {
        case 'degree':
          setData({ degree: '' } as ISearchDegree)
          break
        case 'course':
          setData({ course: '' } as ISearchCourse)
          break
      }
      setResetFields(false)
    }
  }, [resetFields, setResetFields, type])

  if (!data) {
    return <LoadingIndicator />
  }

  const dataValue = (data as { degree?: string; course?: string })[type]

  const add = async () => {
    if (dataValue?.length) {
      switch (type) {
        case 'degree':
          setData({ degree: '' } as ISearchDegree)
          break
        case 'course':
          setData({ course: '' } as ISearchCourse)
          break
      }
      onAdd(data)
    }
  }

  return (
    <DataContext data={data} onSubmit={add} localeBase="resources.searchEmployees" schema={schema} onChange={setData}>
      <Grid container alignItems="flex-end" spacing={2}>
        <Grid size={noButton ? 12 : 10}>
          {type === 'course' && (
            <DataEditContextTextField<ISearchCourse> field="course" fullWidth insetLabel={insetLabel} />
          )}
          {type === 'degree' && (
            <DataEditContextTextField<ISearchDegree> field="degree" fullWidth insetLabel={insetLabel} />
          )}
        </Grid>
        {!noButton && (
          <Grid size={2}>
            <AddButton
              testId={`button-education-search-requirement-add-${type}`}
              valid={!!dataValue || (dataValue?.length ?? 0) > 1}
              clickAction={add}
              tooltip={t('add')}
            />
          </Grid>
        )}
      </Grid>
    </DataContext>
  )
}

export default EducationSearchRequirement
